import {
  Button,
  ButtonVariant,
  ColorClass,
  Icon,
  IconButton,
  IconButtonVariant,
  IconIdentifier,
  OverlayPopover,
  Pill,
  PillVariant,
  Placement,
  Spinner,
  SpinnerVariant,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { isNil } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import { useAppSelector } from 'src/app/hooks';
import { PrintWrapper } from 'src/map-3d/components/map-3d-container/print-wrapper';
import {
  selectMap3dSidebar,
  selectMap3dWorkspace,
} from 'src/map-3d/shared/map-3d-slices';
import { getPointCloudProperties } from '../../../map-3d-workspace/shared/helpers/point-cloud-helpers';
import { CrossSectionProfile } from './cross-section-profile';
import { downloadAsDXF } from './download-handler';
import {
  calculateSlope,
  getPointText,
  getSlopeDetailsText,
} from './slope-tool/helpers';
import type {
  CrossSectionPoint,
  GraphViewerProps,
  SlopeToolState,
} from './types';

export const GraphViewer: React.FC<GraphViewerProps> = ({
  data,
  isLoading,
  isGraphError,
  isVisible,
  isExpanded,
  onClose,
  onToggleExpand,
  pathStartEnd,
  progressPercentage,
  highlightConfig,
}) => {
  // States.
  const [isSlopeToolActive, setIsSlopeToolActive] = useState(false);
  const [showDownloadPopover, setShowDownloadPopover] = useState(false);
  const [slopeToolState, setSlopeToolState] = useState<SlopeToolState>({
    pointA: null,
    pointB: null,
  });

  // Refs.
  const profileRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayTarget = useRef<HTMLButtonElement>(null);
  const printElementRef = useRef<HTMLDivElement>(null);

  // Selectors.
  const { workspaceLayers, selectedPointCloudLayerId } =
    useAppSelector(selectMap3dWorkspace);
  const { activeSidebarOption } = useAppSelector(selectMap3dSidebar);

  const selectedPointCloudLayer = useMemo(() => {
    if (isNil(selectedPointCloudLayerId)) {
      return null;
    }
    return workspaceLayers[selectedPointCloudLayerId] ?? null;
  }, [workspaceLayers, selectedPointCloudLayerId]);

  const pointCloudProperties = getPointCloudProperties(selectedPointCloudLayer);

  const containerClass = classNames([
    'graph-viewer',
    {
      'graph-viewer--expanded': isExpanded,
      'graph-viewer--hidden': !isVisible,
      'graph-viewer--sidebar-closed': !activeSidebarOption?.isHidden,
    },
  ]);

  useEffect(() => {
    if (isLoading) {
      setIsSlopeToolActive(false);
      setSlopeToolState({ pointA: null, pointB: null });
    }
  }, [isLoading]);

  // Handlers.
  const handlePrint = useReactToPrint({
    content: () => printElementRef.current,
    documentTitle: `Cross Section_${selectedPointCloudLayer?.site?.name}_${selectedPointCloudLayer?.iteration?.name}`,
  });

  const handleDownloadPDF = () => {
    if (isNil(data)) {
      return;
    }
    handlePrint();
    setShowDownloadPopover(false);
  };

  const handleDownloadDXF = () => {
    if (isNil(data) || isNil(selectedPointCloudLayer)) {
      return;
    }
    downloadAsDXF(data, selectedPointCloudLayer);
    setShowDownloadPopover(false);
  };

  const handleSlopeToolToggle = () => {
    setIsSlopeToolActive(!isSlopeToolActive);

    if (isSlopeToolActive) {
      setSlopeToolState({ pointA: null, pointB: null });
    }
  };

  const handleSlopePointClick = (point: CrossSectionPoint) => {
    if (!isSlopeToolActive || !data) {
      return;
    }

    const { pointA, pointB } = slopeToolState;

    if (pointA && pointB) {
      setSlopeToolState({
        pointA: point,
        pointB: null,
      });
    } else if (!pointA) {
      setSlopeToolState((prev) => ({
        ...prev,
        pointA: point,
      }));
    } else {
      setSlopeToolState((prev) => ({
        ...prev,
        pointB: point,
      }));
    }
  };

  const renderSlopeCalculationContainer = () => {
    const { pointA, pointB } = slopeToolState;
    const slopeInfo = calculateSlope(pointA, pointB);
    const slopeDetailsText = getSlopeDetailsText(slopeInfo);

    return (
      <div className="graph-viewer__header__calculate-slope__container">
        <Pill className="graph-viewer__header__calculate-slope__pill">
          Point A:{' '}
          <span className="graph-viewer__header__calculate-slope__pill-text">
            {getPointText(pointA)}
          </span>{' '}
          <Icon identifier={IconIdentifier.MarkerA} size={12} /> | Point B:{' '}
          <span className="graph-viewer__header__calculate-slope__pill-text">
            {getPointText(pointB)}
          </span>{' '}
          <Icon identifier={IconIdentifier.MarkerB} size={12} />
        </Pill>

        <Pill
          className="graph-viewer__header__calculate-slope__pill"
          variant={PillVariant.Info}
        >
          ∆x:{' '}
          <span className="graph-viewer__header__calculate-slope__pill-text">
            {slopeDetailsText.deltaX}
          </span>
        </Pill>

        <Pill
          className="graph-viewer__header__calculate-slope__pill"
          variant={PillVariant.Info}
        >
          ∆y:{' '}
          <span className="graph-viewer__header__calculate-slope__pill-text">
            {slopeDetailsText.deltaY}
          </span>
        </Pill>

        <Pill
          className="graph-viewer__header__calculate-slope__pill"
          variant={PillVariant.Info}
        >
          Slope:{' '}
          <span className="graph-viewer__header__calculate-slope__pill-text">
            {slopeDetailsText.slope}
          </span>
        </Pill>

        <Pill
          className="graph-viewer__header__calculate-slope__pill slope-tool-exit-btn"
          variant={PillVariant.Error}
          onClick={handleSlopeToolToggle}
          leftIconIdentifier={IconIdentifier.Slope}
          iconSize={16}
        >
          Exit Slope Tool
        </Pill>
      </div>
    );
  };

  // Renders.
  const renderDownloadPopover = () => {
    return (
      <div className="download-popover">
        <Button
          variant={ButtonVariant.Link}
          className="download-popover__item"
          onClick={handleDownloadDXF}
          disabled={!data}
        >
          Download .DXF
        </Button>
        <Button
          variant={ButtonVariant.Link}
          className="download-popover__item"
          onClick={handleDownloadPDF}
          disabled={!data}
        >
          Download .PDF
        </Button>
      </div>
    );
  };

  const renderHeader = () => (
    <Card.Header className="graph-viewer__header">
      <div className="graph-viewer__title">Graph Viewer - Cross Section</div>

      <div className="graph-viewer__actions">
        {pathStartEnd && (
          <div className="graph-viewer__line-endpoints">
            <div className="graph-viewer__line-endpoints__start">
              <Icon identifier={IconIdentifier.ChevronSmallLeft} size={14} />
              <span>
                From Pos: {pathStartEnd.start.lat.toFixed(6)},{' '}
                {pathStartEnd.start.lng.toFixed(6)}
              </span>
            </div>
            <div className="graph-viewer__line-endpoints__end">
              <span>
                To Pos: {pathStartEnd.end.lat.toFixed(6)},{' '}
                {pathStartEnd.end.lng.toFixed(6)}
              </span>
              <Icon identifier={IconIdentifier.ChevronSmallRight} size={14} />
            </div>
          </div>
        )}

        {!isNil(data) && (
          <>
            {isSlopeToolActive ? (
              renderSlopeCalculationContainer()
            ) : (
              <IconButton
                iconIdentifier={IconIdentifier.Slope}
                variant={IconButtonVariant.Secondary}
                iconProps={{ colorClass: ColorClass.Neutral300 }}
                iconSize={16}
                onClick={handleSlopeToolToggle}
                disabled={!data}
              />
            )}

            {/* Download button */}
            <IconButton
              iconIdentifier={IconIdentifier.Download}
              variant={IconButtonVariant.Secondary}
              iconProps={{ colorClass: ColorClass.Neutral300 }}
              iconSize={16}
              ref={overlayTarget}
              onClick={() => {
                setShowDownloadPopover(!showDownloadPopover);
              }}
              disabled={!data}
            />
            <OverlayPopover
              popoverProps={{ className: 'graph-viewer__download-overlay' }}
              target={overlayTarget.current}
              show={showDownloadPopover}
              body={renderDownloadPopover()}
              placement={Placement.BottomEnd}
              onHide={() => {
                setShowDownloadPopover(false);
              }}
            />

            {/* Expand button */}
            <IconButton
              iconIdentifier={
                isExpanded ? IconIdentifier.Collapse : IconIdentifier.Expand
              }
              variant={IconButtonVariant.Secondary}
              iconProps={{ colorClass: ColorClass.Neutral300 }}
              iconSize={16}
              onClick={onToggleExpand}
            />
          </>
        )}

        {/* Close button */}
        <IconButton
          iconIdentifier={IconIdentifier.Cross}
          variant={IconButtonVariant.Secondary}
          iconProps={{ colorClass: ColorClass.Neutral300 }}
          onClick={onClose}
          iconSize={16}
        />
      </div>
    </Card.Header>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="graph-viewer__loading">
          <Spinner variant={SpinnerVariant.Primary} />
          <span className="graph-viewer__loading-percentage">
            {progressPercentage && progressPercentage > 0
              ? `${progressPercentage}%`
              : '0%'}
          </span>
        </div>
      );
    }

    if (isGraphError) {
      return (
        <div className="graph-viewer__error">
          <Icon identifier={IconIdentifier.GraphOff} size={70} />
          <div className="graph-viewer__error-text">
            <span className="graph-viewer__error-text__head">
              Cross Section Generation Failed.
            </span>
            <span className="graph-viewer__error-text__sub-head">
              Failed to generate the cross section graph. Please try again.
            </span>
          </div>
        </div>
      );
    }

    if (isNil(data) || data.count === 0) {
      return (
        <div className="graph-viewer__empty">
          <Icon identifier={IconIdentifier.GraphOff} size={70} />
          <div className="graph-viewer__empty-text">
            <span className="graph-viewer__empty-text__head">
              You&apos;ve not generated any graphs yet.
            </span>
            <span className="graph-viewer__empty-text__sub-head">
              Please generate one to see it here.
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="graph-viewer__graph-container" ref={profileRef}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
          <CrossSectionProfile
            data={data}
            containerRef={containerRef}
            pointCloudProperties={pointCloudProperties}
            highlightConfig={highlightConfig}
            isSlopeToolActive={isSlopeToolActive}
            slopeToolState={slopeToolState}
            onSlopePointClick={handleSlopePointClick}
            onPointClick={(point) => {
              if (isSlopeToolActive) {
                handleSlopePointClick(point);
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <PrintWrapper
      reportTitle="Point Cloud Cross Section Report"
      ref={printElementRef}
    >
      <Card className={containerClass}>
        {renderHeader()}
        <Card.Body className="graph-viewer__body">{renderContent()}</Card.Body>
      </Card>
    </PrintWrapper>
  );
};
